import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from 'reactstrap';
import PageBreadcrumb from '../../Components/Common/PageBreadcrumb';
import DataTable from 'react-data-table-component';
import ActionButton from '../../Components/Common/ActionButton';
import DeleteModal from '../../Components/Common/DeleteModal';
import ListPager from '../../Components/Common/ListPager';
import {
  ActiveFilterChips,
  ClearFiltersButton,
  ListSearch,
} from '../../Components/Common/ListFilters';
import { EmptyState, SkeletonRows } from '../../Components/Common/ListStates';
import notify, { MESSAGES } from '../../utils/notify';
import '../TenderOpening/components/TenderOpening.css';
import axios from 'axios';
import { api } from '../../config';
import { getAuthToken } from '../../auth/tokenvalidation';
import { FormGrid, TextAreaField, TextField } from '../../Components/Form';

const ProcurementList = () => {
  const token = getAuthToken();

  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [modal, setModal] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalproduct, setModalProduct] = useState(false);
  const [productFormData, setProductFormData] = useState({
    name: null,
    description: null,
    id: null,
  });

  const [filterText, setFilterText] = useState('');
  const [search, setSearch] = useState('');
  const requestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const startEntry = totalRows === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endEntry = Math.min(currentPage * rowsPerPage, totalRows);

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const handleDeleteInvoice = async () => {
    if (invoice) {
      try {
        const response = await fetch(`${api.API_URL}/product/${invoice.id}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! status: ${response.status}`
          );
        }

        notify.success('Product deleted successfully.');
        setDeleteModal(false);
        setInvoice(null);
        fetchData(currentPage, rowsPerPage, token, search);
      } catch (err) {
        notify.apiError(err, 'delete product');
      }
    }
  };

  const toggleproductmodal = () => {
    setModal(!modal);
  };

  const columns = [
    {
      id: 'name',
      name: 'Product Name',
      selector: row => row.name,
      sortable: true,
      width: '40%',
      cell: row => (
        <span className='to-title' title={String(row.name ?? '')}>
          {row.name ?? '-'}
        </span>
      ),
    },

    {
      id: 'description',
      name: 'Product Description',
      selector: row => row.description,
      sortable: true,
      width: '50%',
      cell: row => (
        <span className='pv-desc' title={String(row.description ?? '')}>
          {row.description ?? '-'}
        </span>
      ),
    },

    {
      name: <span className='text-center w-100'>Action</span>,
      width: '10%',
      center: true,
      cell: row => (
        <div className='d-flex gap-1 justify-content-center w-100'>
          <ActionButton
            variant='icon'
            size='sm'
            icon='ri-edit-line'
            label='Edit'
            onClick={() => handleUpdateModal(row.id)}
          />
          <ActionButton
            variant='icon'
            size='sm'
            danger
            icon='ri-delete-bin-line'
            label='Delete'
            confirm={false}
            onClick={() => handleDeleteModal(row.id)}
          />
        </div>
      ),
    },
  ];

  document.title = 'Data Product';

  const handleChange = e => {
    const { name, value } = e.target;
    setProductFormData({ ...productFormData, [name]: value });
  };

  // Debounce typing so each keystroke is not a request.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(filterText.trim());
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [filterText]);

  useEffect(() => {
    fetchData(currentPage, rowsPerPage, token, search);
  }, [currentPage, rowsPerPage, token, search]);

  const handleFilterChange = e => {
    setFilterText(e.target.value);
    // Clearing applies at once; typing waits for the debounce.
    if (!e.target.value) {
      setSearch('');
      setCurrentPage(1);
    }
  };

  const clearFilter = () => {
    setFilterText('');
    setSearch('');
    setCurrentPage(1);
  };

  const fetchData = async (currentPage, rowsPerPage, token, search) => {
    const reqId = ++requestRef.current;
    setLoading(true);
    fetch(
      `${api.API_URL}/product?PageNumber=${currentPage}&PageSize=${rowsPerPage}${search ? `&Search=${encodeURIComponent(search)}` : ''}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      }
    )
      .then(data => data.json())
      .then(
        result => {
          if (reqId !== requestRef.current) return; // stale response
          setData({
            items: result.data.items || [],
          });
          setTotalRows(result.data.totalItems || 0);
        },
        _error => {
          // Handle error appropriately
        }
      )
      .catch(_error => {
        // Handle error silently or show appropriate message
      })
      .finally(() => {
        if (reqId === requestRef.current) setLoading(false);
      });
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  const handleNewModal = () => {
    setProductFormData({
      name: null,
      description: null,
      id: null,
    });
    productmodal();
  };

  const handleUpdateModal = async (mid = null) => {
    await fetchProductDetail(mid);
    productmodal();
  };

  const productmodal = () => {
    setModalProduct(!modalproduct);
  };

  const selectedFieldsProduct = {
    name: productFormData.name,
    description: productFormData.description,
  };

  const handleAddDataProduct = async () => {
    try {
      await axios.post(`${api.API_URL}/product`, selectedFieldsProduct, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success('Product added successfully.');
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  const fetchProductDetail = async mid => {
    try {
      const response = await axios.get(`${api.API_URL}/product/${mid}`, {
        withXSRFToken: true,
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: `application/json`,
        },
      });
      const data = response?.data?.data ?? response?.data ?? response;
      setProductFormData({
        name: data.name || null,
        description: data.description || null,
        id: data.id || null,
      });
    } catch (error) {
      // Handle error silently or show appropriate message
    }
  };

  const handleUpdDataProduct = async mid => {
    try {
      await axios.put(`${api.API_URL}/product/${mid}`, selectedFieldsProduct, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      notify.success(MESSAGES.updated);
    } catch (error) {
      notify.apiError(error, 'save data');
    } finally {
      fetchData(currentPage, rowsPerPage, token, search);
    }
  };

  const filterChips = [
    filterText && {
      key: 'search',
      label: 'Search',
      value: filterText,
      onRemove: () => handleFilterChange({ target: { value: '' } }),
    },
  ].filter(Boolean);

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
          title='Delete this product?'
          message='It will be permanently deleted. This cannot be undone.'
          subject={invoice?.name}
          confirmLabel='Delete product'
        />
        <Container fluid>
          <div className='to-page email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4'>
                <div className='to-hero mb-4'>
                  <PageBreadcrumb
                    fallback={{ label: 'Product', to: '/apps-product/view' }}
                  />

                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <span className='to-hero-icon'>
                      <i className='ri-box-3-line' aria-hidden='true' />
                    </span>
                    <div className='flex-grow-1'>
                      <h4 className='to-hero-title'>Product</h4>
                      <p className='to-hero-subtitle'>
                        Maintain the products available when preparing
                        procurements.
                      </p>
                    </div>
                    <div className='d-flex gap-2 flex-wrap'>
                      <button
                        type='button'
                        className='to-hero-btn'
                        onClick={() =>
                          fetchData(currentPage, rowsPerPage, token, search)
                        }
                      >
                        <i className='ri-refresh-line' aria-hidden='true' />
                        Refresh
                      </button>
                      <button
                        type='button'
                        className='to-hero-btn to-hero-btn--solid'
                        onClick={() => handleNewModal()}
                      >
                        <i className='ri-add-line' aria-hidden='true' />
                        Create New Product
                      </button>
                    </div>
                  </div>
                </div>

                <div className='to-panel'>
                  <div className='to-toolbar'>
                    <ListSearch
                      value={filterText}
                      onChange={handleFilterChange}
                      placeholder='Search products...'
                      ariaLabel='Search products'
                    />
                    <ClearFiltersButton
                      count={filterChips.length}
                      onClick={clearFilter}
                    />
                    <span className='to-toolbar-meta'>
                      {loading
                        ? 'Loading...'
                        : `Showing ${startEntry}–${endEntry} of ${totalRows} products`}
                    </span>
                  </div>

                  <ActiveFilterChips chips={filterChips} />

                  <div className='to-table-wrap'>
                    <DataTable
                      columns={columns}
                      data={data.items}
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId='name'
                      defaultSortAsc
                      progressPending={loading}
                      progressComponent={<SkeletonRows />}
                      noDataComponent={
                        <EmptyState
                          title='No product found'
                          emptyHint='No products have been registered yet. Products added here become selectable in procurement documents.'
                          filteredHint={`No product matches "${filterText}".`}
                          hasFilters={Boolean(filterText)}
                          onClear={clearFilter}
                          action={{
                            label: 'Create New Product',
                            onClick: handleNewModal,
                          }}
                        />
                      }
                      customStyles={{
                        headCells: {
                          style: { paddingLeft: '12px', paddingRight: '12px' },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                          },
                        },
                        table: { style: { width: '100%' } },
                      }}
                    />
                  </div>

                  <ListPager
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    isLoading={loading}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handlePerRowsChange}
                  />
                </div>
              </div>
            </div>
          </div>

          <Modal
            isOpen={modalproduct}
            role='dialog'
            autoFocus={true}
            centered
            id='addAddressModal'
            toggle={toggleproductmodal}
            data-bs-backdrop='static'
            size='sm'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => {
                setModalProduct(!modalproduct);
              }}
            >
              <span className='modal-title text-info fw-bold'>
                {productFormData.id !== null ? 'Edit Product' : 'Add Product'}
              </span>
            </ModalHeader>

            <ModalBody>
              <FormGrid columns={1}>
                <TextField
                  label='Product name'
                  name='name'
                  placeholder='e.g. Pertamax'
                  value={productFormData.name}
                  onChange={handleChange}
                />
                <TextAreaField
                  label='Description'
                  name='description'
                  rows={5}
                  value={productFormData.description}
                  onChange={handleChange}
                />
              </FormGrid>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => {
                  setModalProduct(!modalproduct);
                }}
              >
                Close
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={async () => {
                  await (productFormData.id !== null
                    ? handleUpdDataProduct(productFormData.id)
                    : handleAddDataProduct());
                  setModalProduct(false);
                }}
              >
                {productFormData.id !== null ? 'Update' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProcurementList;
